import { useState } from "react";
import SEO from "../SEO/SEO";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    machinery: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit Indian phone number";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Project details are required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message =
        "Please provide more details (at least 10 characters)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus("");
    try {
      const API_URL = process.env.REACT_APP_API_URL || "/api";
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          machinery: formData.machinery,
          message: formData.message,
          type: "Machinery Inquiry",
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          machinery: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
        console.error("Server error:", result.error);
      }
    } catch (error) {
      console.error("Network error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section
      id="contact"
      className="w-full bg-white py-10 px-4 sm:px-6 lg:px-8"
    >
      <SEO
        title="Contact | Nexora Machinery"
        description="Contact Nexora Machinery for industrial machinery sales and support."
        keywords="Contact Nexora Machinery"
        path="/contact"
      />
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-6 py-2 mb-6">
            <div className="w-2 h-2 bg-[#00d9ff] rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-[#002946]">
              GET IN TOUCH
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#002946] mb-6">
            Let's Start Your <span className="text-[#00d9ff]">Project</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to upgrade your machinery? Contact us for expert consultation
            and quotes
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 lg:p-10 shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold text-[#002946] mb-2">
              Send Inquiry
            </h3>
            <p className="text-gray-600 mb-8">
              We'll get back to you within 24 hours
            </p>
            {submitStatus === "success" && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
                ✅ Thank you! Your inquiry has been sent successfully. We'll
                contact you soon.
              </div>
            )}
            {submitStatus === "error" && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                ❌ Failed to send your inquiry. Please try again or contact us
                directly.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#002946] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] focus:border-transparent transition-all duration-200 bg-white ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#002946] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] focus:border-transparent transition-all duration-200 bg-white ${
                      errors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#002946] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] focus:border-transparent transition-all duration-200 bg-white ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#002946] mb-2">
                  Machinery Interest
                </label>
                <select
                  name="machinery"
                  value={formData.machinery}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] focus:border-transparent transition-all duration-200 bg-white"
                  disabled={isSubmitting}
                >
                  <option value="">Select machinery type</option>
                  <option value="CNC Machines">CNC Machines</option>
                  <option value="Lathe Machines">Lathe Machines</option>
                  <option value="Milling Machines">Milling Machines</option>
                  <option value="Drilling Machines">Drilling Machines</option>
                  <option value="Other Equipment">Other Equipment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#002946] mb-2">
                  Project Details *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  required
                  placeholder="Tell us about your requirements..."
                  className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] focus:border-transparent transition-all duration-200 bg-white resize-none ${
                    errors.message ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isSubmitting}
                ></textarea>
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 transform shadow-lg hover:shadow-xl ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#002946] text-white hover:bg-[#004e7c] hover:scale-105"
                }`}
              >
                {isSubmitting ? (
                  <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-xl shadow-2xl">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00d9ff]"></div>
                        <span className="text-[#002946] font-semibold">
                          Sending your inquiry...
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  "Get Free Consultation"
                )}
              </button>
            </form>
          </div>
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-[#002946] to-[#004e7c] rounded-2xl p-6 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-2xl">📞</span>
                </div>
                <h3 className="text-lg font-bold mb-2">Call Us</h3>
                <p className="text-blue-100">+91 9150620187</p>
                <p className="text-blue-200 text-sm mt-1">Mon-Sat, 9AM-6PM</p>
              </div>
              <div className="bg-gradient-to-br from-[#00d9ff] to-[#0099cc] rounded-2xl p-6 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-2xl">✉️</span>
                </div>
                <h3 className="text-lg font-bold mb-2">Email Us</h3>
                <p className="text-blue-50">info@thenexora.co.in</p>
                <p className="text-blue-100 text-sm mt-1">Quick response</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-[#002946] mb-6">
                Visit Our Office
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#00d9ff] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-lg">📍</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#002946]">
                      Head Office
                    </h4>
                    <p className="text-gray-600">
                      Peenya Industrial Area, Peenya Industrial Area, Bengaluru
                      – 560058, Karnataka, India
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#00d9ff] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-lg">⏰</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#002946]">
                      Business Hours
                    </h4>
                    <p className="text-gray-600">
                      Monday - Saturday: 9:00 AM - 6:00 PM
                    </p>
                    <p className="text-gray-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200">
              <iframe
                title="Nexora Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.351056940709!2d76.9629!3d11.0184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba85859c4c3a123%3A0x9e04a63df39a5e93!2sCoimbatore!5e0!3m2!1sen!2sin!4v1689833098735!5m2!1sen!2sin"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
